import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import defer from 'lodash/defer';
import {
  Outlet, useLocation, useParams, useSearchParams,
} from 'react-router-dom';
import { getVenueReservationsThunk } from '@resy/ui-kitchen';
import AccountPageNav from './AccountPageNav';
import useScrolledState from './useScrolledState';
import { selectIsConcierge, selectIsGlobalDiningAccess } from '../../../../../ui-kitchen/src/reducers/userSlice';
import AmexAdsContainer from '../../containers/AmexAdsContainer/AmexAdsContainer';

const AccountPageWrapper = (props) => {
  const {
    $location,
    $rootScope,
    $window,
    Analytics,
    favorite,
    isConcierge,
    isGdaUser,
    isLg,
    isMd,
    dispatch,
  } = props;
  const { pathname } = useLocation();
  const active = pathname.substring(1);
  const isScrolled = useScrolledState(isLg, $window, () => $rootScope.$apply());
  const paramKeys = {
    MODAL: 'modal',
    GDA_ELIGIBLE: 'gda-eligibility',
  };
  const pathParams = useParams();
  const modalType = pathParams[paramKeys.MODAL]
    ? pathParams[paramKeys.MODAL].toLowerCase()
    : undefined;
  const [searchParams] = useSearchParams(); // see https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
  const isGdaEligible = searchParams.has(paramKeys.GDA_ELIGIBLE) && searchParams.get(paramKeys.GDA_ELIGIBLE) === 'true';
  const selectedDate = $rootScope?.searchProps?.searchFields?.datepicker?.model;
  const flushRoute = () => defer(() => $rootScope.$apply());
  const ref = useRef(null);

  const getQueuedData = (keys) => {
    let payload = null;
    keys.map((key) => {
      const search = $location.search();
      const val = search[key];
      if (val) {
        $location.search(key, null);
        payload = {
          ...payload,
          [key]: val,
        };
      }
      return true;
    });
    return payload;
  };

  const setQueuedData = (payload) => {
    /* istanbul ignore else */
    if (payload) {
      Object.keys(payload).map((key) => {
        $location.search(key, payload[key]);
        return true;
      });
    }
  };

  const showAuthModal = () => {
    $rootScope.$broadcast('MODAL', 'signin');
  };

  const presentCancelation = (reservation) => {
    /* istanbul ignore else */
    if (reservation.conciergeResyToken) {
      $location.search('conciergeToken', reservation.conciergeResyToken);
    }
    $location.search('cancelToken', reservation.token);

    $rootScope.$broadcast('MODAL', 'reservation-cancel');
  };

  // eslint-disable-next-line no-shadow
  const loadReservations = (venueId, selectedDate, selectedQuantity, resyToken) => {
    const params = {
      venue_id: venueId,
      day: moment(selectedDate).format('YYYY-MM-DD'),
      party_size: selectedQuantity,
      resy_token: resyToken,
    };
    return dispatch(getVenueReservationsThunk({ params }));
  };

  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, [pathname]);

  // Reservations sections use infinite scroll, so an anchored bottom ad
  // would be unreachable. Sidenav ad on `lg+` still renders via AccountPageNav.
  const isInfiniteScrollSection = active === 'account/reservations-and-notify'
    || active === 'account/concierge-reservations';

  return (
    <div className="resy-account">
      {!isLg && (
        <AccountPageNav
          $rootScope={$rootScope}
          Analytics={Analytics}
          active={active}
          isLg={isLg}
          isScrolled={isScrolled}
          isConcierge={isConcierge}
          isGdaUser={isGdaUser}
        />
      )}
      <article className="container ResyAccount">
        <section className="row">
          {isLg && (
            <AccountPageNav
              $rootScope={$rootScope}
              Analytics={Analytics}
              active={active}
              isLg={isLg}
              isScrolled={isScrolled}
              isConcierge={isConcierge}
              isGdaUser={isGdaUser}
            />
          )}
          {isLg && <div className="ResyAccount__background" />}
          <div className="col col-lg-9 ResyAccount__detail-wrapper" tabIndex="-1" ref={ref}>
            <Outlet
              context={{
                context: 'resy.com',
                favoriteVenues: favorite,
                flushRoute,
                getQueuedData,
                isGdaEligible,
                isMobile: !isMd,
                loadReservations,
                modalType,
                presentCancelation,
                selectedDate,
                setQueuedData,
                showAuthModal,
                store: $rootScope.reduxStore,
              }}
            />
          </div>
        </section>
      </article>
      {!isLg && !isInfiniteScrollSection && (
        <AmexAdsContainer page="account_page_bottom" routeKey={active} />
      )}
    </div>
  );
};

AccountPageWrapper.propTypes = {
  $location: PropTypes.shape({
    search: PropTypes.func.isRequired,
  }).isRequired,
  $rootScope: PropTypes.shape({
    $apply: PropTypes.func,
    $broadcast: PropTypes.func,
    $on: PropTypes.func,
    searchProps: PropTypes.shape({
      searchFields: PropTypes.shape({
        datepicker: PropTypes.shape({
          model: PropTypes.shape({}),
        }),
      }),
    }),
    reduxStore: PropTypes.shape({}),
  }).isRequired,
  $window: PropTypes.shape({
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
    scrollY: PropTypes.number,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  isConcierge: PropTypes.bool.isRequired,
  isGdaUser: PropTypes.bool.isRequired,
  isLg: PropTypes.bool.isRequired,
  isMd: PropTypes.bool.isRequired,
  favorite: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const { matchMedia: { lg, md }, favorite } = state;
  return ({
    isConcierge: selectIsConcierge(state),
    isGdaUser: selectIsGlobalDiningAccess(state),
    isLg: lg,
    isMd: md,
    favorite,
  });
};

export { mapStateToProps, AccountPageWrapper as ComponentUnderTest };
export default connect(mapStateToProps)(AccountPageWrapper);
